import type { ReactNode } from 'react';

interface CpmSelectionBarProps {
    count: number;
    children: ReactNode;
    helperText?: string;
    warning?: string;
    /**
     * Noun for the count label — design: “2 brands selected”.
     * Defaults to “record(s)”.
     */
    noun?: string;
    /** When set, shows a Clear control after the count (Master Editor design). */
    onClear?: () => void;
}

export default function CpmSelectionBar({
    count,
    children,
    helperText = 'Use the checkboxes in the list to change what is selected',
    warning,
    noun,
    onClear,
}: CpmSelectionBarProps) {
    if (count <= 0) return null;

    const nounLabel =
        noun != null && noun !== ''
            ? count === 1
                ? noun.replace(/s$/, '') || noun
                : noun.endsWith('s')
                  ? noun
                  : `${noun}s`
            : count === 1
              ? 'record'
              : 'records';

    return (
        <div
            className="cpm-animate-fade-in flex items-center flex-wrap text-white"
            style={{
                backgroundImage: 'var(--cpm-gradient-hero)',
                padding: '12px 18px',
                gap: 14,
                borderRadius: 12,
                boxShadow: '0 8px 20px -6px rgba(14,8,88,.45)',
            }}
        >
            <span
                className="tabular text-white"
                style={{ fontSize: 15, fontWeight: 800, letterSpacing: '-0.01em' }}
            >
                {count} {nounLabel} selected
            </span>
            {onClear != null ? (
                <button
                    type="button"
                    onClick={onClear}
                    className="border-0 bg-transparent p-0 cursor-pointer"
                    style={{
                        fontSize: 12.5,
                        fontWeight: 700,
                        color: 'rgba(255,255,255,.7)',
                    }}
                >
                    Clear
                </button>
            ) : (
                <span className="w-px h-[22px] bg-white/20" />
            )}
            <span
                style={{
                    fontSize: 11.5,
                    fontWeight: 600,
                    color: 'rgba(255,255,255,.62)',
                }}
            >
                {helperText}
            </span>
            <div className="ml-auto flex items-center gap-2.5 flex-wrap">
                {warning ? (
                    <span className="inline-flex items-center gap-1.5 text-[12px] font-bold text-[#FDE68A]">
                        <svg
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                        >
                            <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3" />
                            <path d="M12 9v4M12 17h.01" strokeLinecap="round" />
                        </svg>
                        {warning}
                    </span>
                ) : null}
                {children}
            </div>
        </div>
    );
}
